type EventTargetLike = {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
};

type DocumentLike = EventTargetLike & {
  visibilityState?: DocumentVisibilityState;
};

type AuthResumeCoordinatorOptions = {
  document: DocumentLike;
  window: EventTargetLike;
  refresh: () => Promise<void>;
  isInitialized: () => boolean;
  isAuthenticated: () => boolean;
  retryDelaysMs?: readonly number[];
};

const DEFAULT_PENDING_AUTH_RETRY_DELAYS_MS = [1_000, 3_000, 10_000, 30_000] as const;

export function createAuthResumeCoordinator(options: AuthResumeCoordinatorOptions): { destroy: () => void } {
  let refreshPromise: Promise<void> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;
  let destroyed = false;
  const retryDelaysMs = options.retryDelaysMs ?? DEFAULT_PENDING_AUTH_RETRY_DELAYS_MS;

  const isPending = (): boolean => !options.isInitialized() && !options.isAuthenticated();

  const clearRetryTimer = (): void => {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const schedulePendingRetry = (): void => {
    if (destroyed || !isPending() || retryTimer !== null || retryIndex >= retryDelaysMs.length) {
      return;
    }

    const delayMs = retryDelaysMs[retryIndex];
    retryIndex += 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      runRefresh();
    }, delayMs);
  };

  const runRefresh = (): void => {
    if (destroyed || refreshPromise || (!options.isAuthenticated() && options.isInitialized())) {
      return;
    }

    refreshPromise = options.refresh()
      .catch(() => undefined)
      .finally(() => {
        refreshPromise = null;
        if (isPending()) {
          schedulePendingRetry();
        } else {
          retryIndex = 0;
          clearRetryTimer();
        }
      });
  };

  const runWakeRefresh = (): void => {
    if (isPending()) {
      retryIndex = 0;
      clearRetryTimer();
    }
    runRefresh();
  };

  const handleVisibilityChange = (): void => {
    if (!options.document.visibilityState || options.document.visibilityState === 'visible') {
      runWakeRefresh();
    }
  };

  const handleWake = (): void => {
    runWakeRefresh();
  };

  options.document.addEventListener('visibilitychange', handleVisibilityChange);
  options.window.addEventListener('pageshow', handleWake);
  options.window.addEventListener('online', handleWake);

  if (isPending()) {
    runRefresh();
  }

  return {
    destroy() {
      destroyed = true;
      clearRetryTimer();
      options.document.removeEventListener('visibilitychange', handleVisibilityChange);
      options.window.removeEventListener('pageshow', handleWake);
      options.window.removeEventListener('online', handleWake);
    }
  };
}
